/**
 * Aggregation engine — pure, deterministic roll-ups for the Sprint Board's
 * insights: sprint progress, sprint health, estimation accuracy, and goal
 * progress.
 *
 * These power the "Done" and "Learn" steps and the sprint summary/insights:
 *   • sprintProgress   — completed effort vs committed effort.
 *   • sprintHealth     — on_track / at_risk from remaining work vs remaining
 *                        capacity, plus blockers.
 *   • estimateAccuracy — actual vs estimate across closed-out records.
 *   • goalProgress     — completion of a goal's linked sprint tasks.
 *
 * All effort is in ideal engineering days. Ratios are guarded against divide-
 * by-zero (0 committed → ratio 0). Task inputs are minimal structural subsets
 * of the extended {@link Task} so the engine stays decoupled yet type-linked.
 *
 * Pure TS — no React, no I/O.
 */

import type { Task } from '../../tasks/types';
import type { HistoricalEstimate } from '../models/types';

/** The task fields the progress/health roll-ups need. */
export type ProgressTask = Pick<
  Task,
  'boardStatus' | 'effortDays' | 'blocked' | 'goalId'
>;

/** Rounds to 2 decimals. */
function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/** Effort of a task in ideal days (missing/invalid → 0). */
function taskDays(task: Pick<Task, 'effortDays'>): number {
  const d = task.effortDays;
  return typeof d === 'number' && Number.isFinite(d) && d > 0 ? d : 0;
}

/** True when a task is done (DONE lane). */
function isDone(task: Pick<Task, 'boardStatus'>): boolean {
  return task.boardStatus === 'DONE';
}

// ---------------------------------------------------------------------------
// Sprint progress
// ---------------------------------------------------------------------------

/** Sprint progress: completed effort vs committed effort. */
export interface SprintProgress {
  /** Total committed effort (all tasks) in ideal days. */
  committedDays: number;
  /** Completed effort (DONE tasks) in ideal days. */
  completedDays: number;
  /** Completed / committed, clamped to [0, 1] (0 committed → 0). */
  ratio: number;
  /** ratio × 100, rounded to a whole percent. */
  percentComplete: number;
  /** Count of tasks. */
  taskCount: number;
  /** Count of DONE tasks. */
  doneCount: number;
}

/**
 * Computes sprint progress from a sprint's tasks: the share of committed effort
 * that is complete. When effort totals to 0, falls back to a count-based ratio
 * so a sprint of unestimated tasks still shows movement.
 */
export function sprintProgress(tasks: ProgressTask[]): SprintProgress {
  const list = tasks ?? [];
  const committed = round2(list.reduce((sum, t) => sum + taskDays(t), 0));
  const completed = round2(
    list.filter(isDone).reduce((sum, t) => sum + taskDays(t), 0),
  );
  const doneCount = list.filter(isDone).length;
  const taskCount = list.length;

  let ratio: number;
  if (committed > 0) {
    ratio = completed / committed;
  } else if (taskCount > 0) {
    ratio = doneCount / taskCount; // effort-free fallback
  } else {
    ratio = 0;
  }
  ratio = Math.max(0, Math.min(1, ratio));

  return {
    committedDays: committed,
    completedDays: completed,
    ratio: round2(ratio),
    percentComplete: Math.round(ratio * 100),
    taskCount,
    doneCount,
  };
}

// ---------------------------------------------------------------------------
// Sprint health
// ---------------------------------------------------------------------------

/** Health verdict for a sprint. */
export type SprintHealth = 'on_track' | 'at_risk';

/** Sprint health result with the signals behind the verdict. */
export interface SprintHealthResult {
  health: SprintHealth;
  /** Remaining (not-DONE) work in ideal days. */
  remainingWorkDays: number;
  /** Remaining team capacity in ideal days (members × remaining working days). */
  remainingCapacityDays: number;
  /** Number of currently-blocked, not-DONE tasks. */
  blockedCount: number;
  /** Human-readable reasons the verdict is at_risk (empty when on_track). */
  reasons: string[];
}

/**
 * Determines sprint health. A sprint is `at_risk` when the remaining work
 * exceeds the remaining team capacity, OR when any not-DONE task is blocked;
 * otherwise `on_track`. Blockers on already-DONE tasks are ignored.
 *
 * @param tasks               the sprint's tasks
 * @param memberCount         number of engineers on the sprint
 * @param remainingWorkingDays working days left in the sprint
 */
export function sprintHealth(
  tasks: ProgressTask[],
  memberCount: number,
  remainingWorkingDays: number,
): SprintHealthResult {
  const list = tasks ?? [];
  const remainingWork = round2(
    list.filter((t) => !isDone(t)).reduce((sum, t) => sum + taskDays(t), 0),
  );
  const members = Number.isFinite(memberCount) && memberCount > 0 ? memberCount : 0;
  const workingDays =
    Number.isFinite(remainingWorkingDays) && remainingWorkingDays > 0
      ? remainingWorkingDays
      : 0;
  const remainingCapacity = round2(members * workingDays);
  const blockedCount = list.filter((t) => !isDone(t) && t.blocked).length;

  const reasons: string[] = [];
  if (remainingWork > remainingCapacity) {
    reasons.push(
      `Remaining work (${remainingWork}d) exceeds remaining capacity (${remainingCapacity}d).`,
    );
  }
  if (blockedCount > 0) {
    reasons.push(
      `${blockedCount} blocked task${blockedCount === 1 ? '' : 's'}.`,
    );
  }

  return {
    health: reasons.length > 0 ? 'at_risk' : 'on_track',
    remainingWorkDays: remainingWork,
    remainingCapacityDays: remainingCapacity,
    blockedCount,
    reasons,
  };
}

// ---------------------------------------------------------------------------
// Estimate accuracy
// ---------------------------------------------------------------------------

/** Estimate-vs-actual accuracy across closed-out records. */
export interface EstimateAccuracy {
  /** Sum of final estimates in ideal days. */
  totalEstimateDays: number;
  /** Sum of actual effort in ideal days. */
  totalActualDays: number;
  /** actual / estimate (0 estimate → 0). >1 = under-estimated, <1 = over-estimated. */
  ratio: number;
  /** Number of records included. */
  sampleSize: number;
  /** Directional read of the ratio for display. */
  bias: 'under_estimated' | 'over_estimated' | 'accurate' | 'no_data';
}

/**
 * Aggregates estimation accuracy over historical records: the ratio of total
 * actual effort to total final-estimate effort. A ratio > 1 means the team
 * tends to under-estimate; < 1 means it over-estimates. Within ±5% is treated
 * as `accurate`.
 */
export function estimateAccuracy(
  records: Pick<HistoricalEstimate, 'finalEstimateDays' | 'actualEffortDays'>[],
): EstimateAccuracy {
  const list = records ?? [];
  const totalEstimate = round2(
    list.reduce((sum, r) => sum + (Number(r.finalEstimateDays) || 0), 0),
  );
  const totalActual = round2(
    list.reduce((sum, r) => sum + (Number(r.actualEffortDays) || 0), 0),
  );
  const ratio = totalEstimate > 0 ? round2(totalActual / totalEstimate) : 0;

  let bias: EstimateAccuracy['bias'];
  if (list.length === 0 || totalEstimate === 0) {
    bias = 'no_data';
  } else if (ratio > 1.05) {
    bias = 'under_estimated';
  } else if (ratio < 0.95) {
    bias = 'over_estimated';
  } else {
    bias = 'accurate';
  }

  return {
    totalEstimateDays: totalEstimate,
    totalActualDays: totalActual,
    ratio,
    sampleSize: list.length,
    bias,
  };
}

// ---------------------------------------------------------------------------
// Goal progress
// ---------------------------------------------------------------------------

/** Progress toward a goal from its linked sprint tasks. */
export interface GoalProgress {
  goalId: string;
  /** Committed effort of the goal's linked tasks in ideal days. */
  committedDays: number;
  /** Completed effort of the goal's linked tasks in ideal days. */
  completedDays: number;
  /** completed / committed, clamped to [0, 1]. */
  ratio: number;
  /** ratio × 100, rounded. */
  percentComplete: number;
  /** Count of the goal's linked tasks. */
  taskCount: number;
  /** Count of the goal's DONE tasks. */
  doneCount: number;
}

/**
 * Computes progress toward a single goal from the tasks linked to it (by
 * goalId). Delegates to {@link sprintProgress} over the filtered subset so the
 * effort/count fallback behaves identically.
 */
export function goalProgress(
  goalId: string,
  tasks: ProgressTask[],
): GoalProgress {
  const linked = (tasks ?? []).filter((t) => t.goalId === goalId);
  const progress = sprintProgress(linked);
  return {
    goalId,
    committedDays: progress.committedDays,
    completedDays: progress.completedDays,
    ratio: progress.ratio,
    percentComplete: progress.percentComplete,
    taskCount: progress.taskCount,
    doneCount: progress.doneCount,
  };
}
